import {
  CarriedInterestDayCountFormat,
  CarriedInterestFeeAllocationMethod,
  PreferredReturnBasis,
  PreferredReturnCalculationEnd,
  PreferredReturnAccrualPeriod,
  PreferredReturnAccrualMethod,
  WaterfallStructure,
  PreferredReturnCompoundingPeriod,
  CapitalContributionType,
} from 'further-types/lp/carry-settings';
import { z } from 'zod';

export const CarriedInterestPartnerSettings = z
  .object({
    carryPartnerName: z
      .string()
      .trim()
      .min(1, 'Carry partner name is required'),
    carryPartnerFundCommitment: z.number().min(0).optional(),
    capitalContributionType: z
      .nativeEnum(CapitalContributionType)
      .optional()
      .nullable(),
    capitalContributionAmountLcy: z.number().min(0).optional().nullable(),
    capitalContributionPercentage: z
      .number()
      .min(0)
      .max(99.99)
      .optional()
      .nullable(),
    loanCommitmentLcy: z.number().min(0).optional().nullable(),
  })
  .refine(
    (data) => {
      if (
        data.capitalContributionType === CapitalContributionType.FixedAmount
      ) {
        return (
          data.capitalContributionAmountLcy !== null &&
          data.capitalContributionAmountLcy !== undefined
        );
      }
      if (
        data.capitalContributionType ===
        CapitalContributionType.PercentageOfTotalCapital
      ) {
        return (
          data.capitalContributionPercentage !== null &&
          data.capitalContributionPercentage !== undefined
        );
      }
      return true;
    },
    {
      message:
        'Please enter a value for the selected capital contribution type.',
      path: ['capitalContributionAmountLcy'],
    },
  );

export const OverallCarriedInterestSettings = z.object({
  dayCount: z.nativeEnum(CarriedInterestDayCountFormat),
  feeAllocationMethod: z.nativeEnum(CarriedInterestFeeAllocationMethod),
  waterfallStructure: z
    .array(z.nativeEnum(WaterfallStructure))
    .min(1)
    .refine((value) => {
      return value.includes(WaterfallStructure.ExcessProceeds);
    }, 'Excess proceeds must be included in the waterfall structure')
    .refine((value) => {
      const preferredReturnIndex = value.indexOf(
        WaterfallStructure.PreferredReturn,
      );
      if (preferredReturnIndex < 0) return true;

      const carriedInterestPartnerCatchupIndex = value.indexOf(
        WaterfallStructure.CarriedInterestPartnerCatchup,
      );

      if (carriedInterestPartnerCatchupIndex < 0) return true;

      return carriedInterestPartnerCatchupIndex > preferredReturnIndex;
    }, 'The Preferred return must be placed above the Preferred return CP catch-up in the waterfall. Please review the waterfall and try again.'),
});

export const CarriedInterestFeeTemplatesSettings = z.object({
  carriedInterestFeeTemplates: z
    .array(
      z
        .object({
          _id: z.string().optional(),
          name: z.string().min(1),
          carriedInterestRates: z.array(
            z.object({
              rate: z.number().min(0),
              dpiThreshold: z.number().optional().nullable(),
            }),
          ),
          carriedInterestPartnerCatchupBetweenRateThresholds: z.boolean(),
          preferredReturnRate: z.number().min(0),
          preferredReturnAccrualMethod: z.nativeEnum(
            PreferredReturnAccrualMethod,
          ),
          preferredReturnAccrualPeriod: z.nativeEnum(
            PreferredReturnAccrualPeriod,
          ),
          preferredReturnCompoundingPeriod: z
            .nativeEnum(PreferredReturnCompoundingPeriod)
            .optional()
            .nullable(),
          preferredReturnCalculationEnd: z.nativeEnum(
            PreferredReturnCalculationEnd,
          ),
          preferredReturnBasis: z.nativeEnum(PreferredReturnBasis),
          partnerCatchupSpeed: z.number().min(0),
          isDefault: z.boolean().optional(),
        })
        .superRefine((data, ctx) => {
          if (
            data.preferredReturnAccrualMethod ===
              PreferredReturnAccrualMethod.Compound &&
            !data.preferredReturnCompoundingPeriod
          ) {
            ctx.addIssue({
              path: ['preferredReturnCompoundingPeriod'],
              code: z.ZodIssueCode.custom,
              message:
                'Preferred return compounding period is required when preferred return accrual method is compound.',
            });
          }

          if (
            data.preferredReturnAccrualMethod ===
              PreferredReturnAccrualMethod.Simple &&
            data.preferredReturnCalculationEnd !==
              PreferredReturnCalculationEnd.UponFullReturnOfCapital
          ) {
            ctx.addIssue({
              path: ['preferredReturnCalculationEnd'],
              code: z.ZodIssueCode.custom,
              message:
                'Preferred return calculation end must be upon full return of capital when preferred return accrual method is simple.',
            });
          }
        }),
    )
    .min(1),
});
